import React from 'react';
import {
  Action, FormHandlers, Portal, useModal,
} from '@doveit/bricks';
import { Invoice } from '../../../providers/api/dtos';
import { InvoiceStatus } from '../../../domain/types';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import UpsertInvoice from '../upsert-invoice/UpsertInvoice';
import { useComputedInvoiceStatus } from '../../hooks/use-computed-invoice-status/useComputedInvoiceStatus';

interface ChildFnProps {
  emitProformaInvoice: VoidFunction,
}

export interface EmitProformaInvoiceActionProps extends React.AriaAttributes {
  invoice: Invoice,
  canEditInvoiceType?: boolean,
  onSuccess?: (invoice: Invoice) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

const EmitProformaInvoiceAction: React.FC<EmitProformaInvoiceActionProps> = ({
  invoice,
  canEditInvoiceType = false,
  onSuccess,
  children,
}) => {
  const [formActionDisabled, setFormActionDisabled] = React.useState(false);
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const { canBeUpdatedTo } = useComputedInvoiceStatus(invoice);

  const handleSuccess = React.useCallback((updatedInvoice: Invoice) => {
    modal.close();
    onSuccess?.(updatedInvoice);
  }, [modal, onSuccess]);

  if (!canBeUpdatedTo(InvoiceStatus.PROFORMA)) {
    return null;
  }

  return (
    <>
      {children?.({ emitProformaInvoice: modal.open }) || (
        <Action
          label="Emetti proforma"
          size="S"
          aria-label="Azione per emettere il proforma"
          onClick={() => modal.open()}
        />
      )}

      <Portal>
        <SimpleModal
          {...modal}
          title="Emetti proforma"
          aria-label="Modale per emettere il proforma"
          footer={(
            <Action
              label="Emetti proforma"
              color="primary"
              emphasis="high"
              disabled={formActionDisabled}
              aria-label="Azione per confermare l'emissione del proforma"
              onClick={() => formRef.current?.handleSubmit()}
            />
          )}
        >
          <UpsertInvoice
            formRef={formRef}
            invoice={invoice}
            toStatus={InvoiceStatus.PROFORMA}
            onSuccess={handleSuccess}
            onFormStatusChanged={setFormActionDisabled}
            hiddenFields={{
              invoiceId: true,
              paymentMethod: true,
              paidValue: true,
            }}
            disabledFields={{
              type: !canEditInvoiceType,
              property: true,
              reference: true,
            }}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default EmitProformaInvoiceAction;
